/* oxlint-disable tailwindcss/no-unknown-classes -- Semantic duration tokens are not resolved from the CSS theme. */
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { twJoin } from "tailwind-merge";

import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { Overlay } from "@/components/Overlay/Overlay";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { useOnScroll } from "@/hooks/useOnScroll";
import { useSidebarManager } from "@/hooks/useSidebar";

import { IconButton } from "../Button/IconButton";
import { sidebarMobileAnimation } from "./animations";
import { SidebarContext } from "./SidebarContext";
import { SidebarGroupedItem } from "./SidebarGroupedItem";
import { SidebarItem } from "./SidebarItem";
import { type SidebarBodyRootProps, type SidebarItemType } from "./types";

function SidebarRoot({
  children,
  isOpened,
  isCollapsed = false,
  onToggleOpen,
  onToggleCollapse,
  isFaded,
}: SidebarBodyRootProps): React.ReactNode {
  const [isBodyScrolled, bodyScrolledHandlers] = useBool(false);
  const isDesktop = useIsDesktop();

  const contextValue = useMemo(
    () => ({
      isFaded,
      isCollapsed,
      isBodyScrolled,
      onToggleBodyScrolled: bodyScrolledHandlers.set,
      onToggleCollapse,
    }),
    [isFaded, isCollapsed, isBodyScrolled, bodyScrolledHandlers, onToggleCollapse],
  );

  return (
    <SidebarContext value={contextValue}>
      <AnimatePresence>
        {(isDesktop || (!isDesktop && isOpened)) && (
          <>
            <motion.div
              className={twJoin(
                "group/sidebar fixed bottom-0 z-overlay flex max-h-sidebar-compact w-full flex-col overflow-hidden rounded-t-lg transition-width duration-300 md:relative md:z-navigation md:h-screen md:max-h-full md:overflow-visible md:rounded-none",
                isFaded
                  ? "border-r border-border-default bg-background-on-default"
                  : "border-r border-border-default bg-background-default",
                isCollapsed ? "md:w-sidebar-collapsed md:min-w-sidebar-collapsed" : "md:w-sidebar-expanded",
              )}
              variants={sidebarMobileAnimation}
              initial={isDesktop ? "visible" : "hidden"}
              animate="visible"
              exit="hidden"
              key="sidebar"
            >
              {children}
              {/* Collapse trigger */}
              {isDesktop && !isFaded && (
                <aside className="group/sidebar-collapse-trigger pointer-events-none absolute top-0 right-0 z-raised -mr-px h-full w-space-lg">
                  {isDesktop && !isFaded && (
                    <span className="pointer-events-auto absolute top-space-lg right-0 z-sticky translate-x-1/2 -translate-y-1/2">
                      <IconButton
                        size="xs"
                        variant="soft"
                        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        onClick={() => onToggleCollapse(!isCollapsed)}
                        icon={
                          <span className={twResolve("block transition-transform", !isCollapsed && "rotate-180")}>
                            <ChevronRightIcon />
                          </span>
                        }
                        isCircular
                        withTooltip={false}
                      />
                    </span>
                  )}
                  <span className="ml-auto block h-full w-px border-l border-border-default" />
                </aside>
              )}
            </motion.div>
            {!isDesktop && <Overlay onClick={() => onToggleOpen(false)} withPortal />}
          </>
        )}
      </AnimatePresence>
    </SidebarContext>
  );
}
interface SidebarHeaderProps {
  children: React.ReactNode;
}
function SidebarHeader({ children }: SidebarHeaderProps): React.ReactNode {
  const { isBodyScrolled } = useSidebarManager();

  return (
    <header
      className={twJoin(
        "sticky top-0 z-raised w-full border-b border-transparent bg-inherit pt-space-xl pb-space-md shadow-none transition-all",
        isBodyScrolled && "border-border-default shadow-raised",
      )}
    >
      {children}
    </header>
  );
}
interface SidebarBodyProps {
  renderHeader?: () => React.ReactNode;
  children?: React.ReactNode;
  items: SidebarItemType[];
  hasSeparationLineForChildren?: boolean;
}
function SidebarBody({
  renderHeader,
  children,
  items,
  hasSeparationLineForChildren,
}: SidebarBodyProps): React.ReactNode {
  const { isCollapsed, onToggleBodyScrolled } = useSidebarManager();
  const refContainer = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = refContainer.current;

    if (!element) {
      return;
    }

    onToggleBodyScrolled(element.scrollTop > 0);
    setHasMoreBelow(element.scrollHeight - element.scrollTop - element.clientHeight > 1);
  }, [onToggleBodyScrolled]);

  useOnScroll(updateScrollState, refContainer);

  useEffect(() => {
    const element = refContainer.current;

    if (!element) {
      return;
    }

    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(element);
    for (const child of element.children) {
      observer.observe(child);
    }

    return () => observer.disconnect();
  }, [updateScrollState, items, children]);

  return (
    <nav className="relative flex min-h-0 w-full flex-1 flex-col">
      <div
        className={twJoin(
          "flex size-full flex-1 no-scrollbar flex-col items-start overflow-y-auto pb-space-xl",
          isCollapsed ? "px-space-sm" : "px-space-lg",
        )}
        ref={refContainer}
      >
        {renderHeader && <header className="mb-space-md w-full">{renderHeader()}</header>}
        <ul className="flex w-full flex-col gap-space-md">
          {items.map((item) => {
            if (item.type === "group") return <SidebarGroupedItem {...item} key={item.key} />;
            else return <SidebarItem {...item} key={item.key} />;
          })}
        </ul>
        {children && hasSeparationLineForChildren && <hr className="my-space-lg w-full border-border-default" />}
        {children && (
          <footer
            className={twJoin(
              "w-full",
              isCollapsed && "flex justify-center",
              !hasSeparationLineForChildren && "mt-space-md",
            )}
          >
            {children}
          </footer>
        )}
      </div>
      <span
        aria-hidden
        className={twResolve(
          "duration-slow pointer-events-none absolute inset-x-0 bottom-0 h-space-lg bg-linear-to-t from-background-default-inverse/10 to-transparent transition-opacity",
          hasMoreBelow ? "opacity-100" : "opacity-0",
        )}
      />
    </nav>
  );
}
interface SidebarFooterProps {
  children: React.ReactNode;
}
function SidebarFooter({ children }: SidebarFooterProps): React.ReactNode {
  const { isCollapsed } = useSidebarManager();

  return (
    <footer
      className={twJoin(
        "top-0 mt-auto flex w-full overflow-x-clip bg-background-default pt-space-md pb-space-xl",
        isCollapsed ? "justify-center px-space-sm" : "justify-start px-space-lg",
      )}
    >
      {children}
    </footer>
  );
}
export const Sidebar = {
  Root: SidebarRoot,
  Body: SidebarBody,
  Footer: SidebarFooter,
  Header: SidebarHeader,
};
